import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import toast, { Toaster } from 'react-hot-toast';
import { Icon } from '@iconify/react';
import { profileApi } from '../api/client';
import { useAuthStore } from '../store';
import { getApiErrorMessage } from '../api/errors';
import { Image } from '../components/common/NextAdapters';
import { AVATARS, ProfileAvatar, isPresetAvatarId } from '../components/profile-avatar';
import { usePageTitle } from '../hooks/usePageTitle';
import { cn } from '../lib/utils';

const MAX_PROFILES = 5;
const PIN_LENGTH = 4;
const NAME_LIMIT = 30;

const INPUT_CLASS =
  'h-11 w-full rounded-xl border border-white/12 bg-white/5 px-3 text-sm text-foreground outline-none transition placeholder:text-muted-foreground focus:border-primary focus:bg-white/10 focus:ring-2 focus:ring-primary/20';

// The viewer profile a JWT was issued for (its "profileId" claim), or null for an account-level token
// with no profile selected. Read client-side only to decide what to show — the server re-checks
// the same claim on every request, so this can't be used to gain access.
function tokenProfileId(token) {
  try {
    const payload = JSON.parse(atob(token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')));
    return payload.profileId != null ? Number(payload.profileId) : null;
  } catch {
    return null;
  }
}

export default function WhosWatchingPage() {
  usePageTitle("Who's Watching · Kast");
  const navigate = useNavigate();
  const location = useLocation();
  const fromSwitch = Boolean(location.state?.fromSwitch);
  const { user, token, profiles: storedProfiles, setProfiles, selectProfile } = useAuthStore();

  const [profiles, setLocalProfiles] = useState(storedProfiles || []);
  const [loading, setLoading] = useState(true);
  const [manageMode, setManageMode] = useState(false);
  const [pinModal, setPinModal] = useState(null); // { profile }
  const [pinValue, setPinValue] = useState('');
  const [pinError, setPinError] = useState('');
  const [editModal, setEditModal] = useState(null); // { profile } | { isNew: true }
  const [selecting, setSelecting] = useState(null);

  useEffect(() => {
    if (!token) { navigate('/login'); return; }
    let cancelled = false;
    profileApi.list()
      .then(({ data }) => {
        if (cancelled || !data.success) return;
        setLocalProfiles(data.data);
        setProfiles(data.data);
      })
      .catch((err) => toast.error(getApiErrorMessage(err, 'Could not load profiles.')))
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const goAfterSelect = () => {
    // Only the post-login landing sends Admin/ContentCreator straight to their dashboard.
    // Picking "Switch profile" from within the main site should just return there.
    if (fromSwitch) { navigate('/'); return; }
    const role = user?.Role;
    if (role === 'Admin') navigate('/admin');
    else if (role === 'ContentCreator') navigate('/creator');
    else navigate('/');
  };

  const handlePick = async (profile, pin) => {
    setSelecting(profile.Id);
    try {
      const { data } = await profileApi.select(profile.Id, pin);
      if (data.success) {
        selectProfile(data.data.Token, data.data.Profile);
        setPinModal(null);
        setPinValue('');
        setPinError('');
        goAfterSelect();
        return;
      }
    } catch (err) {
      if (err?.response?.status === 423) {
        setPinModal({ profile });
      } else if (err?.response?.status === 401) {
        setPinError('Incorrect PIN. Try again.');
        setPinValue('');
      } else {
        toast.error(getApiErrorMessage(err, 'Could not switch to that profile.'));
      }
    } finally {
      setSelecting(null);
    }
  };

  const handleTileClick = (profile) => {
    if (manageMode && isMainProfile) { setEditModal({ profile }); return; }
    handlePick(profile, undefined);
  };

  const handlePinSubmit = (pin) => {
    if (!pinModal) return Promise.resolve();
    return handlePick(pinModal.profile, pin);
  };

  const handleDelete = async (profile) => {
    if (!window.confirm(`Remove the "${profile.Name}" profile?`)) return;
    try {
      const { data } = await profileApi.remove(profile.Id);
      if (data.success) {
        const next = profiles.filter((p) => p.Id !== profile.Id);
        setLocalProfiles(next);
        setProfiles(next);
        setEditModal(null);
        toast.success('Profile removed.');
      }
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'Could not remove that profile.'));
    }
  };

  const handleSave = async (formValues, avatarFile) => {
    try {
      let savedProfile = null;
      let next = profiles;

      if (editModal?.isNew) {
        const { data } = await profileApi.create(formValues);
        if (data.success) {
          savedProfile = data.data;
          next = [...profiles, savedProfile];
          toast.success('Profile created.');
        }
      } else {
        const { data } = await profileApi.update(editModal.profile.Id, formValues);
        if (data.success) {
          savedProfile = data.data;
          next = profiles.map((p) => (p.Id === editModal.profile.Id ? savedProfile : p));
          toast.success('Profile updated.');
        }
      }

      // Avatar image (if one was chosen) is uploaded as a second step, once we know the profile's id.
      if (savedProfile && avatarFile) {
        try {
          const { data: avatarData } = await profileApi.uploadAvatar(savedProfile.Id, avatarFile);
          if (avatarData.success) {
            savedProfile = avatarData.data;
            next = next.map((p) => (p.Id === savedProfile.Id ? savedProfile : p));
          }
        } catch (avatarErr) {
          toast.error(getApiErrorMessage(avatarErr, 'Profile saved, but the avatar image could not be uploaded.'));
        }
      }

      if (savedProfile) {
        setLocalProfiles(next);
        setProfiles(next);
      }
      setEditModal(null);
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'Could not save that profile.'));
    }
  };

  const canAddMore = profiles.length < MAX_PROFILES;
  // Profiles come back ordered by DateCreated (see ProfileController.GetProfiles), so the first one
  // is the original/main profile on the account — only it is allowed to manage (add/edit/delete) profiles.
  const mainProfileId = profiles[0]?.Id;
  // Decide who is acting from the TOKEN, not from activeProfile: tapping "Switch profile" in the header
  // clears activeProfile (so this picker shows), but the token keeps its profileId claim until another
  // profile is picked — and the server authorises from the token. Going by activeProfile made a Kids
  // session look like the account owner here ("Manage profiles" and the edit pencils appeared). A token
  // with no profile claim is the account holder straight after signing in with their password.
  const actingProfileId = tokenProfileId(token);
  const isMainProfile = actingProfileId === null || actingProfileId === mainProfileId;

  return (
    <div className="relative flex min-h-screen flex-col overflow-hidden bg-background">
      <Toaster
        position="top-center"
        toastOptions={{
          style: {
            background: 'var(--popover)', color: '#fff',
            border: '1px solid rgba(255,255,255,0.15)', borderRadius: '10px', fontSize: '14px',
          },
          success: { iconTheme: { primary: 'var(--primary)', secondary: 'var(--primary-foreground)' } },
          error: { iconTheme: { primary: 'var(--destructive)', secondary: '#fff' } },
        }}
      />

      <div className="pointer-events-none fixed inset-0 z-0">
        <Image src="/bg.png" alt="" fill priority className="object-cover opacity-50" />
        <div className="absolute inset-0 bg-gradient-to-b from-[rgba(7,5,14,0.6)] to-[rgba(7,5,14,0.85)]" />
        <div
          className="absolute -top-36 -left-24 size-[500px] rounded-full opacity-20 mix-blend-screen blur-[120px]"
          style={{ background: 'radial-gradient(circle, var(--flame), transparent 70%)' }}
        />
        <div
          className="absolute -right-24 -bottom-36 size-[450px] rounded-full opacity-20 mix-blend-screen blur-[120px]"
          style={{ background: 'radial-gradient(circle, var(--violet), transparent 70%)' }}
        />
      </div>

      <div className="relative z-10 mx-auto flex h-16 w-full max-w-[1600px] items-center gutter">
        <Image src="/logo-full.png" alt="Kast" width={132} height={40} priority className="h-7 w-auto sm:h-8" />
      </div>

      <main className="relative z-10 flex flex-1 items-center justify-center gutter pb-16">
        {loading ? (
          <Icon icon="ph:spinner-gap-bold" className="size-8 animate-spin text-muted-foreground" />
        ) : (
          <motion.div
            className="flex w-full max-w-4xl flex-col items-center"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
          >
            <h1 className="display text-center text-[clamp(2rem,5vw,3.5rem)]">
              {manageMode ? 'Manage profiles' : "Pick Your Kast!"}
            </h1>
            {/* <p className="mt-3 text-center text-sm text-muted-foreground">
              {manageMode
                ? 'Choose a profile to edit it.'
                : 'Each Kast profile keeps its own recommendations and viewing experience.'}
            </p> */}

            <ul className="mt-10 flex flex-wrap justify-center gap-5 sm:mt-14 sm:gap-8">
              {profiles.map((p) => (
                <li key={p.Id}>
                  <button
                    type="button"
                    onClick={() => handleTileClick(p)}
                    disabled={selecting === p.Id}
                    className="group/profile flex w-24 flex-col items-center gap-3 rounded-xl focus-visible:outline-none disabled:cursor-wait disabled:opacity-50 sm:w-32"
                  >
                    <span className="relative block rounded-xl ring-2 ring-transparent ring-offset-4 ring-offset-background transition duration-200 group-hover/profile:scale-105 group-hover/profile:ring-white/80">
                      <ProfileAvatar
                        avatarId={p.AvatarUrl}
                        className="size-24 rounded-xl sm:size-32"
                        iconClassName="size-1/2"
                      />
                      {manageMode && isMainProfile ? (
                        <span className="absolute inset-0 flex items-center justify-center rounded-xl bg-black/55">
                          <Icon icon="ph:pencil-simple-bold" className="size-8 text-white" />
                        </span>
                      ) : null}
                      {p.Id === mainProfileId ? (
                        <span className="absolute top-1.5 left-1.5 flex size-6 items-center justify-center rounded-full bg-black/60 backdrop-blur-sm">
                          <Icon icon="ph:crown-simple-fill" className="size-3.5 text-primary" />
                          <span className="sr-only">Main profile</span>
                        </span>
                      ) : null}
                      {p.HasPin ? (
                        <span className="absolute top-1.5 right-1.5 flex size-6 items-center justify-center rounded-full bg-black/60 backdrop-blur-sm">
                          <Icon icon="ph:lock-simple-fill" className="size-3.5 text-white" />
                          <span className="sr-only">Locked</span>
                        </span>
                      ) : null}
                      {p.IsKids ? (
                        <span className="absolute -bottom-2 left-1/2 -translate-x-1/2 rounded-full bg-white px-2 py-0.5 text-[10px] font-bold tracking-wide text-black uppercase">
                          Kids
                        </span>
                      ) : null}
                    </span>
                    <span className="w-full truncate text-center text-sm text-muted-foreground transition-colors group-hover/profile:text-foreground sm:text-base">
                      {p.Name}
                    </span>
                  </button>
                </li>
              ))}

              {canAddMore && isMainProfile ? (
                <li>
                  <button
                    type="button"
                    onClick={() => setEditModal({ isNew: true })}
                    className="group/profile flex w-24 flex-col items-center gap-3 rounded-xl focus-visible:outline-none sm:w-32"
                  >
                    <span className="flex size-24 items-center justify-center rounded-xl border-2 border-dashed border-white/20 text-muted-foreground transition duration-200 group-hover/profile:scale-105 group-hover/profile:border-white/60 group-hover/profile:text-foreground sm:size-32">
                      <Icon icon="ph:plus-bold" className="size-9" />
                    </span>
                    <span className="text-sm text-muted-foreground transition-colors group-hover/profile:text-foreground sm:text-base">
                      Add profile
                    </span>
                  </button>
                </li>
              ) : null}
            </ul>

            {isMainProfile ? (
              <button
                type="button"
                onClick={() => setManageMode((v) => !v)}
                className={cn(
                  'mt-12 inline-flex h-10 items-center gap-2 rounded-full px-5 text-sm font-semibold transition sm:mt-16',
                  manageMode
                    ? 'bg-white text-black hover:bg-white/90'
                    : 'border border-white/25 text-muted-foreground hover:border-white/60 hover:text-foreground'
                )}
              >
                {manageMode ? (
                  'Done'
                ) : (
                  <>
                    <Icon icon="ph:pencil-simple-bold" className="size-4" />
                    Manage profiles
                  </>
                )}
              </button>
            ) : null}
          </motion.div>
        )}
      </main>

      {pinModal ? (
        <PinPrompt
          key={pinModal.profile.Id}
          profile={pinModal.profile}
          value={pinValue}
          error={pinError}
          onChange={(next) => { setPinValue(next); if (pinError) setPinError(''); }}
          onClose={() => { setPinModal(null); setPinError(''); setPinValue(''); }}
          onSubmit={handlePinSubmit}
        />
      ) : null}

      {editModal ? (
        <ProfileEditModal
          initial={editModal.isNew ? null : editModal.profile}
          onCancel={() => setEditModal(null)}
          onSave={handleSave}
          onDelete={!editModal.isNew && profiles.length > 1 && editModal.profile.Id !== mainProfileId ? () => handleDelete(editModal.profile) : null}
          isMain={!editModal.isNew && editModal.profile.Id === mainProfileId}
        />
      ) : null}
    </div>
  );
}

function useEscape(onClose) {
  useEffect(() => {
    const onKeyDown = (event) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [onClose]);
}

function Modal({ labelledBy, onClose, onSubmit, children }) {
  useEscape(onClose);

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/70 p-4 backdrop-blur-md animate-in fade-in duration-200"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <form
        role="dialog"
        aria-modal="true"
        aria-labelledby={labelledBy}
        onSubmit={onSubmit}
        className="max-h-[92vh] w-full max-w-md overflow-y-auto rounded-2xl border border-white/12 bg-card p-6 shadow-2xl sm:p-8"
      >
        {children}
      </form>
    </div>
  );
}

function ModalHeader({ id, title, onClose }) {
  return (
    <div className="flex items-center justify-between">
      <h2 id={id} className="display-sm text-xl sm:text-2xl">
        {title}
      </h2>
      <button
        type="button"
        onClick={onClose}
        aria-label="Close"
        className="rounded-full p-1.5 text-muted-foreground transition-colors hover:bg-white/10 hover:text-foreground"
      >
        <Icon icon="ph:x-bold" className="size-4" />
      </button>
    </div>
  );
}

function PinInput({ id, label, value, onChange, autoFocus, invalid, disabled }) {
  const [focused, setFocused] = useState(false);
  const [visible, setVisible] = useState(false);

  return (
    <div className="flex flex-col items-center gap-3">
      <div className="relative">
        <input
          id={id}
          aria-label={label}
          aria-invalid={invalid || undefined}
          type="text"
          inputMode="numeric"
          pattern="[0-9]*"
          autoComplete="one-time-code"
          maxLength={PIN_LENGTH}
          value={value}
          disabled={disabled}
          autoFocus={autoFocus}
          onChange={(event) => onChange(event.target.value.replace(/\D/g, '').slice(0, PIN_LENGTH))}
          onFocus={() => setFocused(true)}
          onBlur={() => setFocused(false)}
          className="absolute inset-0 z-10 h-full w-full cursor-text text-base text-transparent caret-transparent opacity-0 disabled:cursor-not-allowed"
        />
        <div aria-hidden className="flex gap-2.5 sm:gap-3">
          {Array.from({ length: PIN_LENGTH }, (_, index) => {
            const filled = index < value.length;
            const active = focused && !disabled && index === value.length;
            return (
              <span
                key={index}
                className={cn(
                  'flex size-14 items-center justify-center rounded-xl border-2 bg-white/5 text-2xl font-bold transition duration-150 sm:size-16',
                  invalid
                    ? 'border-destructive/70 bg-destructive/10'
                    : active
                      ? 'border-primary bg-white/10 ring-4 ring-primary/20'
                      : filled
                        ? 'border-white/40'
                        : 'border-white/12'
                )}
              >
                {filled ? (
                  visible ? value[index] : <span className="size-3 rounded-full bg-foreground" />
                ) : active ? (
                  <span className="h-6 w-0.5 animate-pulse rounded-full bg-primary" />
                ) : null}
              </span>
            );
          })}
        </div>
      </div>

      <button
        type="button"
        onClick={() => setVisible((prev) => !prev)}
        className="inline-flex items-center gap-1.5 text-xs text-muted-foreground transition-colors hover:text-foreground"
      >
        <Icon icon={visible ? 'ph:eye-slash-bold' : 'ph:eye-bold'} className="size-3.5" />
        {visible ? 'Hide PIN' : 'Show PIN'}
      </button>
    </div>
  );
}

function PinPrompt({ profile, value, error, onChange, onClose, onSubmit }) {
  const [checking, setChecking] = useState(false);

  const handleChange = (next) => {
    onChange(next);
    if (next.length === PIN_LENGTH && !checking) {
      setChecking(true);
      Promise.resolve(onSubmit(next)).finally(() => setChecking(false));
    }
  };

  return (
    <Modal
      labelledBy="profile-unlock-title"
      onClose={onClose}
      onSubmit={(event) => {
        event.preventDefault();
        if (value.length === PIN_LENGTH && !checking) handleChange(value);
      }}
    >
      <ModalHeader id="profile-unlock-title" title="Enter PIN" onClose={onClose} />

      <div className="mt-6 flex flex-col items-center text-center">
        <span className="relative">
          <ProfileAvatar avatarId={profile.AvatarUrl} className="size-16 rounded-xl" />
          <span className="absolute -right-1.5 -bottom-1.5 flex size-6 items-center justify-center rounded-full bg-card ring-2 ring-card">
            <Icon icon="ph:lock-simple-fill" className="size-3.5 text-foreground" />
          </span>
        </span>
        <p className="mt-4 text-sm text-muted-foreground">
          <span className="font-semibold text-foreground">{profile.Name}</span> is locked. Enter
          its {PIN_LENGTH}-digit PIN to continue.
        </p>
      </div>

      <div className="mt-6">
        <PinInput
          id="profile-unlock-pin"
          label={`${PIN_LENGTH}-digit PIN for ${profile.Name}`}
          value={value}
          onChange={handleChange}
          invalid={Boolean(error)}
          disabled={checking}
          autoFocus
        />
      </div>

      <p
        role={error ? 'alert' : undefined}
        className={cn(
          'mt-4 flex h-5 items-center justify-center gap-2 text-sm',
          error ? 'text-destructive' : 'text-muted-foreground'
        )}
      >
        {checking ? (
          <>
            <Icon icon="ph:spinner" className="size-4 animate-spin" />
            Checking…
          </>
        ) : (
          error
        )}
      </p>
    </Modal>
  );
}

function ToggleRow({ id, label, description, checked, onChange }) {
  return (
    <div className="flex items-center justify-between gap-4 rounded-xl border border-white/10 bg-white/3 p-4">
      <div>
        <p id={id} className="text-sm font-semibold text-foreground">
          {label}
        </p>
        <p className="mt-0.5 text-xs text-muted-foreground">{description}</p>
      </div>
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        aria-labelledby={id}
        onClick={() => onChange(!checked)}
        className={cn(
          'relative h-6 w-11 shrink-0 rounded-full transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
          checked ? 'brand-sweep' : 'bg-white/15'
        )}
      >
        <span
          className={cn(
            'absolute top-0.5 left-0.5 size-5 rounded-full bg-white shadow transition-transform duration-200',
            checked && 'translate-x-5'
          )}
        />
      </button>
    </div>
  );
}

const pictureTileClass = (selected) =>
  cn(
    'rounded-lg ring-2 ring-offset-2 ring-offset-card transition focus-visible:outline-none focus-visible:ring-ring',
    selected ? 'ring-white' : 'ring-transparent hover:ring-white/30'
  );

function ProfileEditModal({ initial, onCancel, onSave, onDelete, isMain = false }) {
  const initialHasCustomPhoto = Boolean(initial?.AvatarUrl) && !isPresetAvatarId(initial.AvatarUrl);

  const [name, setName] = useState(initial?.Name ?? '');
  const [avatarId, setAvatarId] = useState(!initialHasCustomPhoto && initial?.AvatarUrl ? initial.AvatarUrl : AVATARS[0].id);
  const [avatarFile, setAvatarFile] = useState(null);
  const [avatarPreview, setAvatarPreview] = useState(initialHasCustomPhoto ? initial.AvatarUrl : null);
  // The main profile manages the others, so it can never be a Kids profile (the server enforces
  // this too) — and an older Kids main profile is converted to Adult on its next save.
  const [isKids, setIsKids] = useState(isMain ? false : initial?.IsKids ?? false);
  const [locked, setLocked] = useState(initial?.HasPin ?? false);
  const [pin, setPin] = useState('');
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [error, setError] = useState('');

  const hadPin = Boolean(initial?.HasPin);

  const handlePresetPick = (id) => {
    setAvatarId(id);
    setAvatarFile(null);
    setAvatarPreview(null);
  };

  const handlePhotoChange = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setAvatarFile(file);
    setAvatarPreview(URL.createObjectURL(file));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    const trimmed = name.trim();
    if (!trimmed) {
      setError('Please give this profile a name');
      return;
    }
    if (locked && (pin || !hadPin) && pin.length !== PIN_LENGTH) {
      setError(`Enter all ${PIN_LENGTH} digits of the PIN`);
      return;
    }

    // A newly-picked file always wins; an untouched custom photo is preserved as-is;
    // otherwise fall back to whichever preset icon is selected.
    const payload = {
      name: trimmed,
      avatarUrl: avatarFile ? avatarId : avatarPreview || avatarId,
      type: isKids ? 'Kids' : 'Adult',
    };
    if (locked && pin) payload.pin = pin;
    else if (!locked && hadPin) payload.pin = '';

    setSaving(true);
    setError('');
    try {
      await onSave(payload, avatarFile);
    } catch (err) {
      setError(err?.message || 'Something went wrong. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    setSaving(true);
    try {
      await onDelete();
    } catch (err) {
      setError(err?.message || 'Something went wrong. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal labelledBy="profile-editor-title" onClose={onCancel} onSubmit={handleSubmit}>
      <ModalHeader id="profile-editor-title" title={initial ? 'Edit profile' : 'Add profile'} onClose={onCancel} />

      <div className="mt-6">
        <label htmlFor="profile-name" className="block text-xs font-semibold tracking-wide text-foreground/80 uppercase">
          Name
        </label>
        <input
          id="profile-name"
          value={name}
          onChange={(event) => setName(event.target.value.slice(0, NAME_LIMIT))}
          autoFocus
          autoComplete="off"
          placeholder="Profile name"
          className={cn(INPUT_CLASS, 'mt-1.5')}
        />
      </div>

      <fieldset className="mt-6">
        <legend className="text-xs font-semibold tracking-wide text-foreground/80 uppercase">Picture</legend>
        <div className="mt-3 grid grid-cols-5 gap-2.5">
          <label
            className={cn(
              pictureTileClass(Boolean(avatarPreview)),
              'relative flex aspect-square cursor-pointer items-center justify-center overflow-hidden rounded-lg bg-white/5'
            )}
          >
            {avatarPreview ? (
              <img src={avatarPreview} alt="Custom profile" className="size-full object-cover" />
            ) : (
              <Icon icon="ph:camera-plus-bold" className="size-1/2 text-muted-foreground" />
            )}
            <input type="file" accept="image/*" onChange={handlePhotoChange} className="hidden" />
          </label>

          {AVATARS.map((avatar) => {
            const selected = !avatarPreview && avatarId === avatar.id;
            return (
              <button
                key={avatar.id}
                type="button"
                onClick={() => handlePresetPick(avatar.id)}
                aria-label={`${avatar.id} avatar`}
                aria-pressed={selected}
                className={pictureTileClass(selected)}
              >
                <ProfileAvatar avatarId={avatar.id} className="aspect-square w-full rounded-lg" />
              </button>
            );
          })}
        </div>
        <p className="mt-2 text-xs text-muted-foreground">Pick an icon or upload your own photo.</p>
      </fieldset>

      <div className="mt-6 space-y-3">
        {isMain ? null : (
          <ToggleRow
            id="kids-label"
            label="Kids profile"
            description="Only shows the Kids menu and titles made for younger viewers."
            checked={isKids}
            onChange={setIsKids}
          />
        )}

        <ToggleRow
          id="lock-label"
          label="Lock with a PIN"
          description={`A ${PIN_LENGTH}-digit PIN is needed to open or edit this profile.`}
          checked={locked}
          onChange={setLocked}
        />

        {locked ? (
          <div className="rounded-xl border border-white/10 bg-white/3 px-4 pt-4 pb-3 text-center">
            <p className="text-xs font-semibold tracking-wide text-foreground/80 uppercase">
              {hadPin ? 'New PIN' : 'Choose a PIN'}
            </p>
            <div className="mt-3">
              <PinInput
                id="profile-pin"
                label={hadPin ? `New ${PIN_LENGTH}-digit PIN` : `${PIN_LENGTH}-digit PIN`}
                value={pin}
                onChange={(next) => { setPin(next); if (error) setError(''); }}
              />
            </div>
            {hadPin ? (
              <p className="mt-2 text-xs text-muted-foreground">Leave empty to keep the current PIN.</p>
            ) : null}
          </div>
        ) : null}
      </div>

      {error ? (
        <p role="alert" className="mt-4 text-sm text-destructive">
          {error}
        </p>
      ) : null}

      <button
        type="submit"
        disabled={saving}
        className="brand-sweep mt-6 flex h-11 w-full cursor-pointer items-center justify-center gap-2 rounded-xl text-sm font-semibold text-white transition hover:opacity-95 disabled:cursor-not-allowed disabled:opacity-50"
      >
        {saving ? <Icon icon="ph:spinner" className="size-5 animate-spin" /> : null}
        {initial ? 'Save changes' : 'Create profile'}
      </button>

      {initial && onDelete ? (
        confirmDelete ? (
          <div className="mt-4 flex items-center justify-between gap-3 rounded-xl border border-destructive/30 bg-destructive/10 p-3">
            <p className="text-sm text-destructive">Delete "{initial.Name}"?</p>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setConfirmDelete(false)}
                className="rounded-lg px-3 py-1.5 text-xs font-semibold text-muted-foreground hover:text-foreground"
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={saving}
                onClick={handleDelete}
                className="rounded-lg bg-destructive px-3 py-1.5 text-xs font-semibold text-white disabled:opacity-50"
              >
                Delete
              </button>
            </div>
          </div>
        ) : (
          <button
            type="button"
            onClick={() => setConfirmDelete(true)}
            className="mt-4 flex w-full items-center justify-center gap-2 rounded-xl py-2 text-sm text-muted-foreground transition-colors hover:text-destructive"
          >
            <Icon icon="ph:trash-simple-bold" className="size-4" />
            Delete profile
          </button>
        )
      ) : null}
    </Modal>
  );
}
